import { useEventListener } from "@vueuse/core";
import { onScopeDispose } from "vue";

/**
 * `Escape` and `Enter` for a dialog shell: `SCADialog`, `SCAConfirmationDialog` and
 * `SCAAvatarDialog` all run on this.
 *
 * It exists because each of the three listened on `window` itself, so a confirmation dialog opened
 * from another dialog delivered one `Enter` to both — the confirmation closed and the dialog
 * underneath submitted. Only the most recently mounted dialog reacts here; the others keep their
 * listener attached but ignore the key until they are on top again.
 *
 * The keystroke is never consumed: an `Enter` inside a text field or a grid still reaches it.
 * A shell whose body owns `Enter` leaves {@link UseDialogKeyboardOptions.onEnter} out.
 */
export interface UseDialogKeyboardOptions {
  /** `Escape` — dismissing the dialog is the shell's business, not this composable's. */
  onEscape: () => void;
  /** `Enter`. Omit for a dialog whose body owns the key (a grid, a multi-line form). */
  onEnter?: () => void;
}

const openDialogs: symbol[] = [];

export default function useDialogKeyboard(options: UseDialogKeyboardOptions): void {
  const dialog = Symbol("dialog");

  openDialogs.push(dialog);
  onScopeDispose(() => {
    const index = openDialogs.indexOf(dialog);
    if (index !== -1) {
      openDialogs.splice(index, 1);
    }
  });

  useEventListener(window, "keydown", (event: KeyboardEvent) => {
    if (openDialogs.at(-1) !== dialog) {
      return;
    }

    switch (event.key) {
      case "Escape":
        options.onEscape();
        break;
      case "Enter":
        options.onEnter?.();
        break;
      default:
        break;
    }
  });
}
